
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ChatFooterErrorPanel } from './ChatFooterErrorPanel';
import { ChatOverlayErrorDialog } from './ChatOverlayErrorDialog';
import { EndChatConfirmation } from './EndChatConfirmation';
import { FeedbackSurvey } from './FeedbackSurvey';
import { useChatBotContext } from '../context/ChatBotContext';
import { ChatbotLocalizedLabels, ChatMessage as ChatMessageType } from '../types';
import { ThankYouModal } from './ThankYouModal';

interface ChatBotWindowProps {
  assistantName: string;
  labels: ChatbotLocalizedLabels;
}

export const ChatBotWindow: React.FC<ChatBotWindowProps> = ({ assistantName, labels }) => {
  const {
    state,
    locale,
    sendMessage,
    retryLastMessage,
    dismissMessage,
    toggleChat,
    endChatFlowState,
    surveyState,
    initiateEndChat,
    cancelEndChat,
    closeThankYou,
    confirmEndChat,
    submitSurvey,
    skipSurvey,
    openInlineFeedbackSurvey,
  } = useChatBotContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const [isMessageFailedModalOpen, setIsMessageFailedModalOpen] = useState(false);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const hasPendingResponseRef = useRef(false);
  const preSendAssistantIdRef = useRef<string | undefined>(undefined);


  const {
    retryErrorMessage,
    systemErrorMessage,
    connectErrorMessage,
    latestAssistantMessageId,
    lastAssistantMessage,
  } = useMemo(() => {
    let retry: ChatMessageType | undefined;
    let system: ChatMessageType | undefined;
    let connect: ChatMessageType | undefined;
    let latestSentId: string | undefined;
    let lastAssistant: ChatMessageType | undefined;

    for (let i = state.messages.length - 1; i >= 0; i -= 1) {
      const msg = state.messages[i];
      if (msg.role !== 'assistant') continue;
      // lastAssistant spans hidden error replies too: they are not rendered but are announced
      if (!lastAssistant) lastAssistant = msg;
      if (msg.status === 'error') {
        if (!retry && msg.action === 'retry_last_message') retry = msg;
        if (!system && msg.action === 'system_error_retry') system = msg;
        if (!connect && msg.action === 'chat_connect_close') connect = msg;
      } else if (!latestSentId && msg.status === 'sent' && msg.contentFormat !== 'html') {
        // hidden messages are all status 'error', so scanning all messages matches visibleMessages
        latestSentId = msg.id;
      }
    }

    return {
      retryErrorMessage: retry,
      systemErrorMessage: system,
      connectErrorMessage: connect,
      latestAssistantMessageId: latestSentId,
      lastAssistantMessage: lastAssistant,
    };
  }, [state.messages]);

  const hiddenErrorMessageIds = new Set(
    [retryErrorMessage?.id, systemErrorMessage?.id, connectErrorMessage?.id].filter(Boolean)
  );
  const visibleMessages = state.messages.filter(msg => !hiddenErrorMessageIds.has(msg.id));

  const onMessageSend = useCallback(async (message: string) => {
    if (retryErrorMessage?.id) dismissMessage(retryErrorMessage.id);
    if (systemErrorMessage?.id) dismissMessage(systemErrorMessage.id);
    if (connectErrorMessage?.id) dismissMessage(connectErrorMessage.id);
    setIsMessageFailedModalOpen(false);

    return sendMessage(message);
  }, [connectErrorMessage?.id, dismissMessage, retryErrorMessage?.id, sendMessage, systemErrorMessage?.id]);


  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, state.isLoading, state.isOpen]);

  // iOS keyboard shrinks only the visual viewport; publish its insets so the fixed window can follow
  useEffect(() => {
    if (!state.isOpen) return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    const root = document.documentElement;
    const syncViewportInsets = () => {
      const bottomInset = Math.max(window.innerHeight - viewport.height - viewport.offsetTop, 0);
      root.style.setProperty('--chatbot-visual-viewport-top', `${Math.max(viewport.offsetTop, 0)}px`);
      root.style.setProperty('--chatbot-visual-viewport-bottom', `${bottomInset}px`);
    };

    syncViewportInsets();
    viewport.addEventListener('resize', syncViewportInsets);
    viewport.addEventListener('scroll', syncViewportInsets);
    return () => {
      viewport.removeEventListener('resize', syncViewportInsets);
      viewport.removeEventListener('scroll', syncViewportInsets);
      root.style.removeProperty('--chatbot-visual-viewport-top');
      root.style.removeProperty('--chatbot-visual-viewport-bottom');
    };
  }, [state.isOpen]);

  useEffect(() => {
    if (!retryErrorMessage?.id) {
      setIsMessageFailedModalOpen(false);
    }
  }, [retryErrorMessage?.id]);

  // On mobile the input is not auto-focused; move focus to the dialog so AT users land inside it
  useEffect(() => {
    if (state.isOpen && isMobile) {
      windowRef.current?.focus();
    }
  }, [state.isOpen]);
  
  // Announce the settled reply once, read from the rendered bubble so speech matches the screen
  useEffect(() => {
    if (!state.isOpen) {
      // the pending flag survives close, or reopening mid-stream re-baselines against itself
      setLiveAnnouncement('');
      return;
    }

    if (state.isResponding) {
      if (!hasPendingResponseRef.current) {
        hasPendingResponseRef.current = true;
        preSendAssistantIdRef.current = lastAssistantMessage?.id;
      }
      setLiveAnnouncement(labels.thinking);
      return;
    }

    if (!hasPendingResponseRef.current) return;

    const messageId = lastAssistantMessage?.id;
    // cancelled/failed send added no reply; clear the flag so a later reset stays silent
    if (!messageId || messageId === preSendAssistantIdRef.current) {
      hasPendingResponseRef.current = false;
      return;
    }

    hasPendingResponseRef.current = false;
    // error replies are kept out of the transcript, so fall back to their raw content
    const rendered = windowRef.current?.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
    const spoken = (rendered?.innerText || lastAssistantMessage?.content || '').trim();
    if (spoken) {
      setLiveAnnouncement(spoken);
    }
  }, [state.isOpen, state.isResponding, lastAssistantMessage, labels.thinking]);

  // Escape closes the panel only when the panel is the nearest dialog; a nested one owns its own
  useEffect(() => {
    if (!state.isOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const target = event.target as HTMLElement | null;
      if (!target || target.closest('[role="dialog"]') !== windowRef.current) return;
      toggleChat();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [state.isOpen, toggleChat]);

  if (!state.isOpen) {
    return null;
  }

  return (
    <div
      ref={windowRef}
      className="chatbot-window"
      role="dialog"
      aria-labelledby="chatbot-window-title"
      tabIndex={-1}
    >
      {/* Header */}
      <div className="chatbot-header">
        <button
          className="chatbot-header-chevron"
          onClick={toggleChat}
          aria-label={labels.minimizeChat}
          title={labels.minimizeChat}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </button>
        <span
          className="chatbot-header-text"
          id="chatbot-window-title"
          role="heading"
          aria-level={2}
        >
          {assistantName}
        </span>
        <button
          className="chatbot-header-end-chat"
          disabled={state.isLoading}
          onClick={initiateEndChat}
          aria-label={labels.endChat}
        >
          {labels.endChat}
        </button>
      </div>

      {/* Messages */}
      <div
        className="chatbot-messages"
        role="region"
        aria-labelledby="chatbot-window-title"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        {visibleMessages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            locale={locale}
            assistantName={assistantName}
            userLabel={labels.userLabel}
            assistantFallbackName={labels.assistantFallbackName}
            imageAltLabel={labels.imageAlt}
            onFailedMessageClick={
              message.role === 'user' && message.status === 'error' && retryErrorMessage
                ? () => setIsMessageFailedModalOpen(true)
                : undefined
            }
            failedMessageActionAriaLabel={labels.messageFailedTitle}
            showFeedbackActions={message.id === latestAssistantMessageId}
            thumbsUpLabel={labels.thumbsUp}
            thumbsDownLabel={labels.thumbsDown}
            onThumbsUp={() => {
              void openInlineFeedbackSurvey();
            }}
            onThumbsDown={() => {
              void openInlineFeedbackSurvey();
            }}
          />
        ))}
        {state.isLoading && (
          <div className="chatbot-message chatbot-message-assistant">
            <div className="chatbot-message-content chatbot-message-content--thinking">
              <div className="chatbot-message-avatar">
                <svg width="24" height="24" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect width="56" height="56" rx="28" fill="var(--chatbot-primary)"/>
                  <path d="M22 34C22.8607 34 23.6241 34.5508 23.8965 35.3672L24.2656 36.4707L24.3516 36.6875C24.5826 37.18 25.0069 37.56 25.5293 37.7344L26.6328 38.1035L26.9258 38.2266C27.5784 38.567 28 39.2467 28 40C28 40.8607 27.4492 41.6241 26.6328 41.8965L25.5293 42.2656C25.0069 42.44 24.5826 42.82 24.3516 43.3125L24.2656 43.5293L23.8965 44.6328C23.6241 45.4492 22.8607 46 22 46C21.2467 46 20.567 45.5784 20.2266 44.9258L20.1035 44.6328L19.7344 43.5293C19.56 43.0069 19.18 42.5826 18.6875 42.3516L18.4707 42.2656L17.3672 41.8965C16.5508 41.6241 16 40.8607 16 40C16 39.1393 16.5508 38.3759 17.3672 38.1035L18.4707 37.7344L18.6875 37.6484C19.18 37.4174 19.56 36.9931 19.7344 36.4707L20.1035 35.3672L20.2266 35.0742C20.567 34.4216 21.2467 34 22 34ZM35 14C35.8335 14 36.5802 14.5166 36.873 15.2969L38.0605 18.4648C39.0914 21.2139 40.9614 23.5706 43.4043 25.1992L45.1094 26.3359L45.3066 26.4863C45.7437 26.8635 46 27.4147 46 28C46 28.5853 45.7437 29.1365 45.3066 29.5137L45.1094 29.6641L43.4043 30.8008C40.9614 32.4294 39.0914 34.7861 38.0605 37.5352L36.873 40.7031C36.5802 41.4834 35.8335 42 35 42C34.2706 42 33.6082 41.6042 33.2578 40.9824L33.127 40.7031L31.9395 37.5352C30.9729 34.9577 29.2692 32.726 27.0469 31.1152L26.5957 30.8008L24.8906 29.6641C24.3342 29.2931 24 28.6687 24 28C24 27.3313 24.3342 26.7069 24.8906 26.3359L26.5957 25.1992L27.0469 24.8848C29.2692 23.274 30.9729 21.0423 31.9395 18.4648L33.127 15.2969L33.2578 15.0176C33.6082 14.3958 34.2706 14 35 14ZM18 10C18.893 10 19.6785 10.5926 19.9238 11.4512L20.332 12.8809L20.4375 13.2168C21.0149 14.8659 22.2865 16.1886 23.9258 16.8262L24.7246 17.1367C25.4934 17.4357 26 18.1752 26 19C26 19.8248 25.4934 20.5643 24.7246 20.8633L23.9258 21.1738C22.2865 21.8114 21.0149 23.1341 20.4375 24.7832L20.332 25.1191L19.9238 26.5488C19.6785 27.4074 18.893 28 18 28C17.2184 28 16.52 27.5463 16.1934 26.8574L16.0762 26.5488L15.668 25.1191C15.1846 23.4275 13.9857 22.0379 12.3965 21.3105L12.0742 21.1738L11.2754 20.8633C10.5066 20.5643 10 19.8248 10 19C10 18.1752 10.5066 17.4357 11.2754 17.1367L12.0742 16.8262L12.3965 16.6895C13.9857 15.9621 15.1846 14.5725 15.668 12.8809L16.0762 11.4512L16.1934 11.1426C16.52 10.4537 17.2184 10 18 10Z" fill="white"/>
                </svg>
              </div>
              <div className="chatbot-message-bubble">
                <div className="chatbot-thinking">
                  <div className="chatbot-thinking-text">{labels.thinking}</div>
                  <span className="chatbot-typing-dots" aria-hidden="true">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chatbot-visually-hidden" role="status" aria-live="polite">
        {liveAnnouncement}
      </div>

      {/* Input */}
      <div className="chatbot-footer">
        {systemErrorMessage && (
          <ChatFooterErrorPanel
            title={labels.errorTitle}
            body={systemErrorMessage.content || labels.errorFallbackMessage}
            dismissLabel={labels.dismiss}
            retryLabel={labels.retry}
            onDismiss={() => dismissMessage(systemErrorMessage.id)}
            onRetry={async () => {
              dismissMessage(systemErrorMessage.id);
              await retryLastMessage();
            }}
          />
        )}
        <div className="chatbot-footer-input-wrapper">
          <ChatInput
            onSend={onMessageSend}
            disabled={state.isLoading}
            placeholder={labels.inputPlaceholder}
            sendMessageAriaLabel={labels.sendMessageAria}
            maxCharsHintTemplate={labels.maxCharsHintTemplate}
            charLimitExceededSuffix={labels.charLimitExceededSuffix}
          />
        </div>
      </div>

      <EndChatConfirmation
        labels={labels}
        isOpen={endChatFlowState === 'confirming'}
        onConfirm={confirmEndChat}
        onCancel={cancelEndChat}
      />

      {surveyState.feedbackSurveyID && (
        <FeedbackSurvey
          questions={surveyState.questions}
          feedbackSurveyID={surveyState.feedbackSurveyID}
          isSubmitting={endChatFlowState === 'submitting'}
          isOpen={endChatFlowState === 'survey' || endChatFlowState === 'submitting'}
          labels={labels}
          onSubmit={submitSurvey}
          onSkip={skipSurvey}
        />
      )}
      
      <ThankYouModal isOpen={endChatFlowState === 'thank-you'} labels={labels} onClose={closeThankYou}/>

      {connectErrorMessage && (
        <ChatOverlayErrorDialog
          variant="connect"
          isOpen={Boolean(connectErrorMessage)}
          title={labels.unableToConnectTitle}
          body={connectErrorMessage.content || labels.unableToConnectBody}
          closeLabel={labels.close}
          onClose={() => dismissMessage(connectErrorMessage.id)}
        />
      )}

      {retryErrorMessage && isMessageFailedModalOpen && (
        <ChatOverlayErrorDialog
          variant="retry"
          isOpen={Boolean(retryErrorMessage && isMessageFailedModalOpen)}
          title={labels.messageFailedTitle}
          body={labels.messageFailedBody}
          closeLabel={labels.close}
          returnToChatLabel={labels.returnToChat}
          retryLabel={labels.retry}
          onClose={() => setIsMessageFailedModalOpen(false)}
          onReturnToChat={() => setIsMessageFailedModalOpen(false)}
          onRetry={async () => {
            dismissMessage(retryErrorMessage.id);
            setIsMessageFailedModalOpen(false);
            await retryLastMessage();
          }}
        />
      )}
    </div>
  );
};
